import { c as _c } from "react/compiler-runtime";
import { Badge, Button, Card, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { SESSION_EARNINGS_FEE_FREE_PERCENT, SESSION_EARNINGS_FEE_PRO_PERCENT, SESSION_EARNINGS_FEE_TEAM_PERCENT, resolveAdvisorSessionEarningsFeePercent } from '@src/components/sco/Advisors/model/access';
import { formatUsd } from '@src/components/sco/Advisors/utils/sessionFormat';
import { usePlanPriceLabels } from '@src/components/sco/Conversion/controller/usePlanPriceLabels';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { LuCheck } from 'react-icons/lu';
type SessionEarningsFeeNoticeProps = {
  feePercent?: number | null;
  /** Listed hourly rate — used for the “You earn $x/hour” line. */
  hourlyRate?: number | null;
  wilUpgradeHref: string;
  teamUpgradeHref: string;
};
type EarnTier = {
  key: 'free' | 'pro' | 'team';
  title: string;
  priceLabel: string;
  fee: number;
  href?: string;
};

/**
 * Expert-facing withhold on paid Sessions. Founders still pay the listed package.
 */
export const SessionEarningsFeeNotice = t0 => {
  const $ = _c(23);
  const {
    feePercent,
    hourlyRate,
    wilUpgradeHref,
    teamUpgradeHref
  } = t0;
  const planPrices = usePlanPriceLabels();
  const fee = resolveAdvisorSessionEarningsFeePercent(feePercent);
  let t1;
  if ($[0] !== hourlyRate) {
    t1 = hourlyRate != null && Number.isFinite(Number(hourlyRate)) ? Number(hourlyRate) : null;
    $[0] = hourlyRate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rate = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      key: "free",
      title: "Free",
      priceLabel: "Included",
      fee: SESSION_EARNINGS_FEE_FREE_PERCENT
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = planPrices.pro || "Startups Pro";
  let t4;
  if ($[3] !== t3 || $[4] !== wilUpgradeHref) {
    t4 = {
      key: "pro",
      title: "Startups Pro",
      priceLabel: t3,
      fee: SESSION_EARNINGS_FEE_PRO_PERCENT,
      href: wilUpgradeHref
    };
    $[3] = t3;
    $[4] = wilUpgradeHref;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = planPrices.team || "Advisor Team";
  let t6;
  if ($[6] !== t5 || $[7] !== teamUpgradeHref) {
    t6 = {
      key: "team",
      title: "Advisor Team",
      priceLabel: t5,
      fee: SESSION_EARNINGS_FEE_TEAM_PERCENT,
      href: teamUpgradeHref
    };
    $[6] = t5;
    $[7] = teamUpgradeHref;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t6) {
    t7 = [t2, t4, t6];
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const tiers = t7;
  const matchedCurrent = tiers.some(tier => tier.fee === fee);
  let t8;
  if ($[12] !== fee || $[13] !== tiers) {
    t8 = tiers.filter(tier_0 => tier_0.fee < fee).map(_temp);
    $[12] = fee;
    $[13] = tiers;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const upgradeFees = t8;
  const nearestUpgradeFee = upgradeFees.length ? Math.max(...upgradeFees) : null;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <VStack align="stretch" gap={1}><Text fontWeight="semibold" textStyle="lg">What you take home</Text><Text color="colorPalette.fg/70" fontFamily="longform" textStyle="sm">Founders pay your listed session price. Your plan is the share of that price you receive.</Text></VStack>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      base: 1,
      md: 3
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== fee || $[18] !== matchedCurrent || $[19] !== nearestUpgradeFee || $[20] !== rate || $[21] !== tiers) {
    t11 = <VStack align="stretch" gap={4}>{t9}<SimpleGrid columns={t10} gap={3}>{tiers.map(tier_2 => {
          const tierKeep = 100 - tier_2.fee;
          const isCurrent = matchedCurrent ? tier_2.fee === fee : false;
          const canUpgrade = tier_2.fee < fee && !!tier_2.href;
          const isNearestUpgrade = canUpgrade && tier_2.fee === nearestUpgradeFee;
          const youEarnPerHour = rate != null && rate > 0 ? Math.round(rate * (tierKeep / 100) * 100) / 100 : null;
          return <Card.Root key={tier_2.key} aria-current={isCurrent ? "true" : undefined} borderWidth={isCurrent ? "2px" : "1px"} height="full" size="sm" variant={isCurrent ? "subtle" : "outline"}><Card.Body gap={4} height="full" justifyContent="space-between"><VStack align="stretch" gap={1}><Card.Title>{tier_2.title}</Card.Title><Text fontWeight="semibold" letterSpacing="tight" lineHeight={1} textStyle="3xl">{tierKeep}%</Text><Text color="colorPalette.fg/70" fontFamily="longform" textStyle="sm">of each session</Text>{youEarnPerHour != null ? <Text color="colorPalette.fg/80" fontFamily="longform" fontWeight="medium" textStyle="sm">You earn {formatUsd(youEarnPerHour)}/hour</Text> : null}</VStack>{isCurrent ? <Badge alignSelf="flex-start" variant="solid"><LuCheck /> Your plan</Badge> : canUpgrade ? <Button asChild={true} size="sm" variant={isNearestUpgrade ? "cta" : "outline"} width="full"><NextLink href={tier_2.href}>{`Unlock · ${tier_2.priceLabel}`}</NextLink></Button> : null}</Card.Body></Card.Root>;
        })}</SimpleGrid></VStack>;
    $[17] = fee;
    $[18] = matchedCurrent;
    $[19] = nearestUpgradeFee;
    $[20] = rate;
    $[21] = tiers;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
};
function _temp(tier_1) {
  return tier_1.fee;
}